<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-lg-12">
        <div class="card" id="licensedAgentsList">
          <div class="card-header border-0">
            <div class="row g-4 align-items-center">
              <div class="col-sm">
                <div>
                  <h5 class="card-title mb-0">Licensed Agents (Call List)</h5>
                </div>
              </div>
            </div>
            
            <!-- Filters -->
            <div class="row mt-3">
              <div class="col-md-3 mb-2">
                <input type="text" class="form-control" placeholder="Search by Keyword (Name, Phone, Email...)" v-model="filterKeyword" @keyup.enter="filterList" />
              </div>
              <div class="col-md-2 mb-2">
                <select class="form-select" v-model="filterStatus" @change="filterList">
                  <option value="">All Statuses</option>
                  <option value="0">New</option>
                  <option value="1">Interested</option>
                  <option value="2">Not Interested</option>
                  <option value="3">Contracted</option>
                </select>
              </div>
              <div class="col-md-2 mb-2">
                <input type="text" class="form-control" placeholder="Search by State" v-model="filterState" @keyup.enter="filterList" />
              </div>
              <div class="col-md-3 mb-2">
                <input type="text" class="form-control" placeholder="Search by City" v-model="filterCity" @keyup.enter="filterList" />
              </div>
              <div class="col-md-2 mb-2">
                <button class="btn btn-primary w-100" @click="filterList">
                  <i class="ri-search-line align-bottom me-1"></i> Search
                </button>
              </div>
            </div>
          </div>
          <div class="card-body">
            <div class="table-responsive table-card mb-4">
              <table class="table align-middle table-nowrap mb-0 table-hover">
                <thead class="table-light">
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Phone</th>
                    <th scope="col">Email</th>
                    <th scope="col">City</th>
                    <th scope="col">State</th>
                    <th scope="col">Zip Code</th>
                    <th scope="col">Status</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(data, index) of list" :key="index">
                    <td>{{ data.firstName }} {{ data.lastName }}</td>
                    <td>{{ formatPhone(data.phoneNumber) }}</td>
                    <td><a :href="'mailto:' + data.email">{{ data.email }}</a></td>
                    <td>{{ data.city }}</td>
                    <td>{{ data.state }}</td>
                    <td>{{ data.postalCode }}</td>
                    <td>
                      <select class="form-select form-select-sm" v-model="data.status" @change="updateStatus(data.ID, data.status)" :disabled="data.status === 3 && !isAdmin">
                        <option :value="0">New</option>
                        <option :value="1">Interested</option>
                        <option :value="2">Not Interested</option>
                        <option :value="3">Contracted</option>
                      </select>
                    </td>
                    <td>
                      <div class="d-flex gap-2">
                        <button class="btn btn-warning btn-sm"
                                data-bs-toggle="modal" href="#open-notes" 
                                title="Notes" @click="getNotes(data.ID)">
                            <i class="ri-sticky-note-fill align-bottom me-1"></i> Notes
                        </button>
                        <button class="btn btn-success btn-sm"
                                title="Convert to Agent" @click="convertToAgent(data.ID)">
                            <i class="ri-user-add-fill align-bottom me-1"></i> Convert
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div class="d-flex justify-content-end mt-3" v-if="list.length >= 0">
              <div class="pagination-wrap hstack gap-2">
                <a
                    class="page-item pagination-prev"
                    :class="{ disabled: page === 1 }"
                    href="#"
                    @click.prevent="changePage(page - 1)"
                >
                  Previous
                </a>
                <ul class="pagination listjs-pagination mb-0">
                  <li
                      :class="{ active: pageNumber === page }"
                      v-for="(pageNumber, index) in visiblePages"
                      :key="index"
                      @click.prevent="changePage(pageNumber)"
                  >
                    <a class="page" href="#">{{ pageNumber }}</a>
                  </li>
                </ul>
                <a
                    class="page-item pagination-next"
                    :class="{ disabled: page >= totalPages }"
                    href="#"
                    @click.prevent="changePage(page + 1)"
                >
                  Next
                </a>
                <span>
                  Total Found: <b>{{ totalCount }}</b>
                </span>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
    
    <!-- Notes Modal -->
    <div class="modal fade" id="open-notes" tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <notes v-if="notesData.length > 0" :dataList="notesData" @onFire="refreshNotes" :idNumber="2"></notes>
        </div>
      </div>
    </div>
    
  </Layout>
</template>

<script>
import Layout from "../../layouts/main.vue";
import PageHeader from "@/components/page-header";
import notes from "../../components/users/notes.vue";
import axios from "axios";
import Swal from "sweetalert2";
import jwt_decode from "jwt-decode";

export default {
  page: {
    title: "Licensed Agents Call List",
  },
  components: {
    Layout,
    PageHeader,
    notes
  },
  data() {
    return {
      title: "Licensed Call List",
      items: [
        { text: "Agents", href: "/" },
        { text: "Licensed Call List", active: true },
      ],
      list: [],
      page: 1,
      perPage: 100,
      totalCount: 0,
      filterKeyword: "",
      filterState: "",
      filterCity: "",
      filterStatus: "0",
      
      // Notes
      notesData: [],
      currentAgentID: null,
      isAdmin: false
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalCount / this.perPage) || 1;
    },
    visiblePages() {
      const pages = [];
      let start = Math.max(1, this.page - 2);
      let end = Math.min(this.totalPages, start + 4);
      if (end - start < 4) {
        start = Math.max(1, end - 4);
      }
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  mounted() {
    this.getList();
    const token = localStorage.getItem('jwt');
    if (token) {
      try {
        const decoded = jwt_decode(token);
        this.isAdmin = !!(decoded.userGroup && (decoded.userGroup.includes('SUPER_ADMIN_GROUP') || decoded.userGroup.includes('ADMIN_GROUP')));
      } catch (e) {
        this.isAdmin = false;
      }
    }
  },
  methods: {
    async getList() {
      try {
        const response = await axios.post("/api/listLicensedAgents", {
          token: localStorage.getItem("jwt"),
          page: this.page,
          limit: this.perPage,
          keyword: this.filterKeyword,
          state: this.filterState,
          city: this.filterCity,
          status: this.filterStatus
        });
        this.list = response.data.data;
        this.totalCount = response.data.total;
      } catch (error) {
        Swal.fire("Error", "Could not load licensed agents.", "error");
      }
    },
    filterList() {
      this.page = 1;
      this.getList();
    },
    changePage(newPage) {
      if (newPage >= 1 && newPage <= this.totalPages && newPage !== this.page) {
        this.page = newPage;
        this.getList();
      }
    },
    formatPhone(phone) {
      if (!phone) return "";
      let p = phone.replace(/\D/g, "");
      if (p.length > 10) p = p.substring(p.length - 10);
      const x = p.match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
      return !x[2] ? x[1] : "(" + x[1] + ") " + x[2] + (x[3] ? "-" + x[3] : "");
    },
    async updateStatus(agentID, status) {
      try {
        const response = await axios.post("/api/updateLicensedAgentStatus", {
          token: localStorage.getItem("jwt"),
          agentID: agentID,
          status: status
        });
        if (response.data.status !== "OK") {
          Swal.fire("Error", response.data.message || "Failed to update status", "error");
        }
      } catch (error) {
        Swal.fire("Error", "Failed to update status.", "error");
      }
    },
    async convertToAgent(agentID) {
      Swal.fire({
        title: "Are you sure?",
        text: "This will create a new CRM Agent user for this contact.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Yes, convert it!"
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const response = await axios.post("/api/convertLicensedAgentToUser", {
              token: localStorage.getItem("jwt"),
              agentID: agentID
            });
            if (response.data.status === "OK") {
              Swal.fire("Converted!", "Agent has been created successfully.", "success");
              this.getList();
            } else if (response.data.message === "The agent must have an email address to be converted into a User.") {
              Swal.fire({
                title: "Email Required",
                text: "Please provide an email address for this agent:",
                input: 'email',
                inputPlaceholder: 'Enter email address',
                showCancelButton: true,
                confirmButtonColor: "#34c38f",
                cancelButtonColor: "#f46a6a",
                confirmButtonText: "Convert"
              }).then(async (emailResult) => {
                if (emailResult.isConfirmed && emailResult.value) {
                  try {
                    const retryResponse = await axios.post("/api/convertLicensedAgentToUser", {
                      token: localStorage.getItem("jwt"),
                      agentID: agentID,
                      email: emailResult.value
                    });
                    if (retryResponse.data.status === "OK") {
                      Swal.fire("Converted!", "Agent has been created successfully.", "success");
                      this.getList();
                    } else {
                      Swal.fire("Error", retryResponse.data.message || "Failed to convert.", "error");
                    }
                  } catch (error) {
                    Swal.fire("Error", "Failed to convert.", "error");
                  }
                }
              });
            } else {
              Swal.fire("Error", response.data.message || "Failed to convert.", "error");
            }
          } catch (error) {
            Swal.fire("Error", "Failed to convert.", "error");
          }
        }
      });
    },
    
    // Notes methods
    async getNotes(agentID) {
      this.currentAgentID = agentID;
      this.notesData = [];
      localStorage.setItem('selectedUser', agentID);
      
      try {
        const response = await axios.post("/api/getLicensedAgentNotes", {
          token: localStorage.getItem("jwt"),
          agentID: agentID
        });
        
        if (response.data && response.data.length >= 2) {
          this.notesData = response.data;
        } else if (response.data === "You do not have the required permissions") {
           Swal.fire("Error", response.data, "error");
        }
      } catch(error) {
        console.error("Error loading notes", error);
      }
    },
    refreshNotes() {
      if (this.currentAgentID) {
        this.getNotes(this.currentAgentID);
      }
    }
  }
};
</script>
